import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
} from "@mui/material";
import PropTypes from "prop-types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { s3baseUrl } from "src/config/config";
import ReactVideoPlayer from "src/components/ReactVideoPlayer/ReactVideoPlayer";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CustomConfirmation from "src/components/ModalPopover/Confirmation";
import { useSnackbar } from "notistack";
import { makeStyles } from "@mui/styles";
import { htmlDecode } from "src/utils/convertHtml";
import {
  deleteExerciseApi,
  exercise_detail_api,
} from "src/DAL/exercise/exercise";

const ITEM_HEIGHT = 48;

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

function ExerciseDetail() {
  const id = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openDelete, setOpenDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lessonDetailInfo, setLessonsDetailInfo] = useState();
  const [exerciseTags, setExerciseTags] = useState([]);
  const [difficultyTags, setDifficultyTags] = useState([]);
  const [mainMuscleTags, setMainMuscleTags] = useState([]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickOpenDelete = () => {
    setOpenDelete(true);
    setAnchorEl(null);
  };

  const handleDelete = async (value) => {
    setOpenDelete(false);
    setIsLoading(true);
    const result = await deleteExerciseApi(id.slug);
    if (result.code === 200) {
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "success" });
      navigate(-1);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setIsLoading(false);
    }
  };

  const handleNavigateEdit = (value) => {
    navigate(`/exercise-edit/${lessonDetailInfo.exercise_slug}`, {
      state: lessonDetailInfo,
    });
  };

  const handleDetail = async () => {
    setIsLoading(true);
    let result = await exercise_detail_api(id.slug);
    if (result.code === 200) {
      setLessonsDetailInfo(result?.exercise);
      setExerciseTags(result?.exercise?.tags?.exercise);
      setDifficultyTags(result?.exercise?.tags?.difficulty);
      setMainMuscleTags(result?.exercise?.tags?.main_muscle);
      setIsLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    handleDetail();
  }, []);

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  return (
    <div className="container">
      <CustomConfirmation
        open={openDelete}
        setOpen={setOpenDelete}
        title={"Are you sure you want to delete Exercise?"}
        handleAgree={handleDelete}
      />
      <div className="row mobile-margin display-flex">
        <div className="col-12 d-flex">
          <IconButton
            className="back-screen-button me-2"
            onClick={() => navigate(-1)}
          >
            <ArrowBackIcon />
          </IconButton>

          <h2>{lessonDetailInfo?.title}</h2>
        </div>
      </div>

      <div className="row section-space">
        <div className="col-9"></div>
        {lessonDetailInfo?.action_by === "coach_user" && (
          <div className="col-3 text-end">
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={open ? "long-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleClick}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              MenuListProps={{
                "aria-labelledby": "long-button",
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              PaperProps={{
                style: {
                  maxHeight: ITEM_HEIGHT * 6.5,
                  width: "30ch",
                  fontSize: "12px",
                },
              }}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={handleNavigateEdit}>
                <EditIcon className="me-3" height="10px" /> Edit
              </MenuItem>
              <MenuItem onClick={handleClickOpenDelete}>
                <DeleteIcon className="me-3" height="10px" />
                Delete
              </MenuItem>
            </Menu>
          </div>
        )}
      </div>
      <div className="row media-margin">
        <div className="col-12 ">
          {lessonDetailInfo?.video_url == "" ? (
            lessonDetailInfo?.gif_url ? (
              <div className="w-100 d-flex justify-content-center">
                <img src={s3baseUrl + lessonDetailInfo?.gif_url} />
              </div>
            ) : (
              <img
                width="100%"
                src={s3baseUrl + lessonDetailInfo?.image?.thumbnail_1}
              />
            )
          ) : (
            <ReactVideoPlayer url={lessonDetailInfo?.video_url} />
          )}
        </div>
        <div className="col-6 section-space">
          <div
            className="d-flex"
            style={{ fontSize: "12px", fontWeight: "bold" }}
          >
            <div className="me-2">Type:</div>
            <div style={{ opacity: "0.6" }}>
              {htmlDecode(location?.state?.title)}
            </div>
          </div>
        </div>
        {(exerciseTags.length > 0 ||
          mainMuscleTags.length > 0 ||
          difficultyTags.length > 0) && (
          <div className="col-12 mt-3 d-flex align-items-start">
            <div className="mt-1"> Tags:</div>
            <Box sx={{ marginTop: 0 }}>
              {exerciseTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ marginX: 0.5, marginY: 0.5 }}
                />
              ))}
              {mainMuscleTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ marginX: 0.5, marginY: 0.5 }}
                />
              ))}
              {difficultyTags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ marginX: 0.5, marginY: 0.5 }}
                />
              ))}
            </Box>
          </div>
        )}
        <div className="col-12 section-space">
          <p>
            <div
              dangerouslySetInnerHTML={{
                __html: location?.state?.instructions,
              }}
            ></div>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ExerciseDetail;
